//#region imports
import React, { useContext, useEffect, useState } from "react";

import {
  AbwesendMitarbeiter,
  ArbeitsplanType,
  selectedDayType,
  Terminarten,
  UserType,
  WorkPlanContainer,
} from "../../utils/types";
import "../../App.css";
import _ from "lodash";
import { BaustellenTerminListItem } from "./BaustellenTerminListItem";
import { GeneralArbeitsplanListItem } from "./GeneralArbeitsplanListItem";
import { KundendienstListItem } from "./KundendienstListItem";
import AuthContext from "../../context/AuthContext";
import { DaySelection } from "./DaySelection";
import { getIsAnyDateInRange } from "../../utils/helperFunctions";
import { KachelBox } from "../common/KachelBox";
import { Typography } from "@mui/material";
import {
  filterAbwesend,
  filterBauArbeitsplane,
  filterBaustellentermiene,
  filterFortbildungen,
  filterPVArbeitsplane,
  filterMontageArbeitsplane,
  filterWartungArbeitsplane,
  filterKundendienstArbeitsplane,
  filterNotdienst,
  groupWorkplan,
} from "./ArbeitsplanFilter";
//#endregion
//#region interfaces

interface ArbeitsplanPageProps {
  data: ArbeitsplanType[];
  mbNummer?: number;
  showArbeitsplane?: boolean;
}
//#endregion

const ArbeitsplanContent: React.FC<ArbeitsplanPageProps> = ({
  data,
  mbNummer,
  showArbeitsplane,
}) => {
  //#region Initializations
  const [bauArbeitsplan, setBauArbeitsplan] = useState<ArbeitsplanType[][]>([]);
  const [weiterbildungArbeitsplan, setWeiterbildungArbeitsplan] = useState<
    ArbeitsplanType[][]
  >([]);
  const [montageArbeitsplan, setMontageArbeitsplan] = useState<
    ArbeitsplanType[][]
  >([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [pvAbteilung, setPVAbteilung] = useState<ArbeitsplanType[][]>([]);
  const [wartung, setWartung] = useState<ArbeitsplanType[][]>([]);
  const [kundendienstArbeitsplan, setKundendienstArbeitsplan] = useState<
    ArbeitsplanType[][]
  >([]);
  const [baustellenTerminArbeitsplan, setBaustellenTerminArbeitsplan] =
    useState<ArbeitsplanType[][]>([]);
  const [abwesend, setAbwesend] = useState<AbwesendMitarbeiter[]>([]);
  const [terminausserHaus, setTerminausserHaus] = useState<
    AbwesendMitarbeiter[]
  >([]);
  const [notdienst, setNotdienst] = useState<ArbeitsplanType[]>([]);
  const [selectedDays, setSelectedDays] = useState<selectedDayType[]>([]);

  const { user } = useContext(AuthContext);
  const currentUser: UserType = user;

  useEffect(() => {
    const getAllUsers = async () => {
      const response = await fetch(`/api/getAllUsers/`);
      const data = await response.json();
      setUsers(data);
    };
    getAllUsers();
  }, []);

  useEffect(() => {
    const getArbeitsplan = async () => {
      let filteredData: ArbeitsplanType[] = data;
      if (selectedDays && selectedDays.length > 0) {
        filteredData = data.filter((plan: ArbeitsplanType) =>
          selectedDays.some((tag) => getIsAnyDateInRange(plan, tag.days))
        );
      }

      let filteredGroupData: ArbeitsplanType[][] = groupWorkplan(filteredData);
      if (mbNummer != null && mbNummer > 0) {
        filteredData = filteredData.filter(
          (x: ArbeitsplanType) =>
            x.mitarbeiter &&
            x.mitarbeiter
              .split(",")
              .some((value) => value && value.trim() === mbNummer.toString())
        );
        filteredGroupData = filteredGroupData.filter((x: ArbeitsplanType[]) =>
          x.some(
            (value) =>
              value &&
              value.mitarbeiter &&
              value.mitarbeiter
                .split(",")
                .some((value) => value && value.trim() === mbNummer.toString())
          )
        );
      }

      // bauArbeitsplan
      setBauArbeitsplan(
        filterBauArbeitsplane(filteredGroupData, mbNummer?.toString())
      );

      // Montage
      setMontageArbeitsplan(
        filterMontageArbeitsplane(filteredGroupData, mbNummer?.toString())
      );

      // PV Arbeitsplan
      setPVAbteilung(
        filterPVArbeitsplane(filteredGroupData, mbNummer?.toString())
      );

      // Wartung
      setWartung(
        filterWartungArbeitsplane(filteredGroupData, mbNummer?.toString())
      );

      // Kundendienst
      setKundendienstArbeitsplan(
        filterKundendienstArbeitsplane(filteredGroupData, mbNummer?.toString())
      );

      // Baustellentermin
      setBaustellenTerminArbeitsplan(
        filterBaustellentermiene(
          filteredGroupData,
          currentUser,
          mbNummer?.toString()
        )
      );

      // fortbildungen
      setWeiterbildungArbeitsplan(
        filterFortbildungen(filteredData, mbNummer?.toString())
      );

      // Notdienst
      setNotdienst(filterNotdienst(filteredData, mbNummer?.toString()));

      // Abwesend
      setAbwesend(
        filterAbwesend(
          filteredData,
          users,
          mbNummer != null && mbNummer > 0
            ? [Terminarten["Urlaub"], Terminarten["Freizeitausgleich"]]
            : [
                Terminarten["Urlaub"],
                Terminarten["Krank"],
                Terminarten["Freizeitausgleich"],
                Terminarten["Schule"],
                Terminarten["Weiterbildung"],
                Terminarten["überbetriebliche Unterweisung"],
                Terminarten["Prüfung"],
                Terminarten["Feiertag"],
                Terminarten["Arbeitsunfall"],
                Terminarten["Vergabetermin"],
              ]
        )
      );

      // Termin außer Haus
      setTerminausserHaus(
        filterAbwesend(filteredData, users, [
          Terminarten["Termin außer Haus"],
          Terminarten["Baustellentermin"],
          Terminarten["Baubesprechung"],
          Terminarten["Besprechungstermin"],
          Terminarten["Vergabetermin"],
        ])
      );
    };

    getArbeitsplan();
  }, [data, selectedDays, users, currentUser]);

  //#endregion

  // #region ExpandCollapse
  const [isExpandedWartung, setIsExpandedWartung] = useState(
    showArbeitsplane ?? false
  );
  const [isExpandedPV, setIsExpandedPV] = useState(showArbeitsplane ?? false);
  const [isExpandedKundendienst, setIsExpandedKundendienst] = useState(
    showArbeitsplane ?? false
  );
  const [isExpandedBaustellentermin, setIsExpandedBaustellentermin] = useState(
    showArbeitsplane ?? false
  );
  const [isExpandedBau, setIsExpandedBau] = useState(showArbeitsplane ?? false);

  const [isExpandedMontage, setIsExpandedMontage] = useState(
    showArbeitsplane ?? false
  );
  const [isExpandedAbwesend, setIsExpandedAbwesend] = useState(
    showArbeitsplane ?? false
  );
  const [isExpandedTerminausserHaus, setIsExpandedTerminausserHaus] = useState(
    showArbeitsplane ?? false
  );
  const [isExpandedNotdienst, setIsExpandedNotdienst] = useState(
    showArbeitsplane ?? false
  );
  const [isExpandedFortbildung, setIsExpandedFortbildung] = useState(
    showArbeitsplane ?? false
  );

  // #endregion

  return (
    <>
      <DaySelection
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        windowSize={window.innerWidth}
        preselectToday={true}
        showWeekNavigation={
          currentUser &&
          (currentUser.bemerkung === "GF" ||
            currentUser.bemerkung === "Personal")
        }
      />
      {montageArbeitsplan?.length > 0 && (
        <KachelBox
          title="Montage"
          isExpanded={isExpandedMontage}
          setIsExpanded={setIsExpandedMontage}
        >
          {montageArbeitsplan.map((arbeitsplanList, index) => (
            <GeneralArbeitsplanListItem
              key={index}
              workPlanList={arbeitsplanList}
              userList={users}
            />
          ))}
        </KachelBox>
      )}
      {bauArbeitsplan?.length > 0 && (
        <KachelBox
          title="Tägliche Fahrten"
          isExpanded={isExpandedBau}
          setIsExpanded={setIsExpandedBau}
        >
          {bauArbeitsplan.map((arbeitsplanList, index) => (
            <GeneralArbeitsplanListItem
              key={index}
              workPlanList={arbeitsplanList}
              userList={users}
            />
          ))}
        </KachelBox>
      )}
      {kundendienstArbeitsplan?.length > 0 && (
        <KachelBox
          title="Kundendienst"
          isExpanded={isExpandedKundendienst}
          setIsExpanded={setIsExpandedKundendienst}
        >
          {kundendienstArbeitsplan.map((abeitsplanList, index) => (
            <KundendienstListItem
              key={index}
              workPlan={abeitsplanList[0]}
              users={users}
            />
          ))}
        </KachelBox>
      )}
      {pvAbteilung?.length > 0 && (
        <KachelBox
          title="PV-Abteilung"
          isExpanded={isExpandedPV}
          setIsExpanded={setIsExpandedPV}
        >
          {pvAbteilung.map((abeitsplanList, index) => (
            <GeneralArbeitsplanListItem
              key={index}
              workPlanList={abeitsplanList}
              userList={users}
            />
          ))}
        </KachelBox>
      )}
      {wartung?.length > 0 && (
        <KachelBox
          title="Wartung"
          isExpanded={isExpandedWartung}
          setIsExpanded={setIsExpandedWartung}
        >
          {wartung.map((abeitsplanList, index) => (
            <GeneralArbeitsplanListItem
              key={index}
              workPlanList={abeitsplanList}
              userList={users}
            />
          ))}
        </KachelBox>
      )}
      {baustellenTerminArbeitsplan?.length > 0 && (
        <KachelBox
          title="Termine"
          isExpanded={isExpandedBaustellentermin}
          setIsExpanded={setIsExpandedBaustellentermin}
        >
          {baustellenTerminArbeitsplan.map((abeitsplanList, index) => (
            <BaustellenTerminListItem
              key={index}
              workPlan={abeitsplanList[0]}
              users={users}
            />
          ))}
        </KachelBox>
      )}
      {mbNummer != null &&
        weiterbildungArbeitsplan != null &&
        weiterbildungArbeitsplan.length > 0 && (
          <KachelBox
            title="Fortbildungen"
            isExpanded={isExpandedFortbildung}
            setIsExpanded={setIsExpandedFortbildung}
          >
            {weiterbildungArbeitsplan.map((arbeitsplanList, index) => (
              <GeneralArbeitsplanListItem
                key={index}
                workPlanList={arbeitsplanList}
                userList={users}
              />
            ))}
          </KachelBox>
        )}
      {notdienst != null && notdienst.length > 0 && (
        <KachelBox
          title="Notdienst"
          isExpanded={isExpandedNotdienst}
          setIsExpanded={setIsExpandedNotdienst}
        >
          {notdienst.map((arbeitsplan, index) => (
            <BaustellenTerminListItem
              key={index}
              workPlan={arbeitsplan}
              users={users}
            />
          ))}
        </KachelBox>
      )}
      {mbNummer == null && terminausserHaus?.length > 0 && (
        <KachelBox
          title="Außer Haus"
          isExpanded={isExpandedTerminausserHaus}
          setIsExpanded={setIsExpandedTerminausserHaus}
        >
          {terminausserHaus.map((abwesendMitarbeiter, index) => {
            return (
              abwesendMitarbeiter.name && (
                <WorkPlanContainer
                  key={index}
                  style={{
                    textAlign: "center",
                  }}
                >
                  <Typography>
                    <b>{abwesendMitarbeiter.name}</b>
                  </Typography>
                  {abwesendMitarbeiter.dateRange &&
                    abwesendMitarbeiter.dateRange != "" && (
                      <Typography variant="body2">
                        {abwesendMitarbeiter.dateRange}
                      </Typography>
                    )}
                </WorkPlanContainer>
              )
            );
          })}
        </KachelBox>
      )}
      {abwesend?.length > 0 && (
        <KachelBox
          title="Abwesend"
          isExpanded={isExpandedAbwesend}
          setIsExpanded={setIsExpandedAbwesend}
        >
          {abwesend.map((abwesendMitarbeiter, index) => {
            return (
              abwesendMitarbeiter.name && (
                <WorkPlanContainer
                  key={index}
                  style={{
                    textAlign: "center",
                  }}
                >
                  <Typography>
                    <b>{abwesendMitarbeiter.name}</b>
                  </Typography>
                  {abwesendMitarbeiter.dateRange &&
                    abwesendMitarbeiter.dateRange != "" && (
                      <Typography variant="body2">
                        {abwesendMitarbeiter.dateRange}
                      </Typography>
                    )}
                </WorkPlanContainer>
              )
            );
          })}
        </KachelBox>
      )}{" "}
    </>
  );
};

export default ArbeitsplanContent;
